import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, RefreshCw, Users, ArrowLeft, LogOut, Lock, BarChart3, Eye, Clock, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Chart configuration constants (moved outside component to avoid re-creation)
const CHART_MARGIN = { top: 5, right: 30, left: 20, bottom: 5 };
const CHART_COLORS = ['#3B82F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6'];
const TOOLTIP_STYLE = { 
  backgroundColor: '#121A2B', 
  border: '1px solid #1E293B',
  borderRadius: '8px',
  color: '#F8FAFC'
};
const Y_AXIS_TICK_STYLE = { fill: '#F8FAFC', fontSize: 12 };

// Login Screen Component
const LoginScreen = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      }, { withCredentials: true });

      if (response.data.success) {
        // Store token in localStorage as backup
        localStorage.setItem('admin_token', response.data.token);
        localStorage.setItem('admin_username', response.data.username);
        onLogin(response.data);
      }
    } catch (err) {
      // Always show exactly "Incorrect credentials" per user requirement
      setError('Incorrect credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1020] flex items-center justify-center p-4">
      <Card className="bg-[#121A2B] border-[#1E293B] p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#3B82F6]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="text-[#3B82F6]" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-[#F8FAFC] mb-2">Admin Login</h1>
          <p className="text-[#94A3B8]">Enter your credentials to access the dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="username" className="text-[#F8FAFC]">Username</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] mt-1"
              placeholder="Enter username"
              required
              data-testid="admin-username-input"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-[#F8FAFC]">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] mt-1"
              placeholder="Enter password"
              required
              data-testid="admin-password-input"
            />
          </div>

          {error && (
            <div className="text-red-400 text-sm text-center" data-testid="login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white py-6"
            disabled={isLoading}
            data-testid="admin-login-btn"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// Blog Stats Tab Component
const BlogStatsTab = ({ authToken }) => {
  const [blogStats, setBlogStats] = useState({ posts: [], summary: {} });
  const [loading, setLoading] = useState(true);

  const loadBlogStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/blog-stats`, {
        headers: { Authorization: `Bearer ${authToken}` },
        withCredentials: true
      });
      setBlogStats(response.data);
    } catch (error) {
      console.error('Error loading blog stats:', error);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadBlogStats();
  }, [loadBlogStats]);

  // Prepare data for chart (top 5 posts)
  const chartData = useMemo(() => blogStats.posts.slice(0, 5).map(post => ({
    name: post.title.length > 25 ? post.title.substring(0, 25) + '...' : post.title,
    views: post.total_views,
    fullTitle: post.title
  })), [blogStats.posts]);

  const formatLastViewed = useCallback((isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }, []);

  if (loading) {
    return (
      <div className="p-12 text-center text-[#94A3B8]">
        <RefreshCw size={48} className="mx-auto mb-4 opacity-50 animate-spin" />
        <p className="text-lg">Loading blog statistics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#121A2B] border-[#1E293B] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#94A3B8] text-sm mb-1">Total Views</p>
              <p className="text-3xl font-bold text-[#F8FAFC]">{blogStats.summary.total_views_all_time || 0}</p>
            </div>
            <Eye className="text-[#3B82F6]" size={32} />
          </div>
        </Card>
        <Card className="bg-[#121A2B] border-[#1E293B] p-6">
          <div>
            <p className="text-[#94A3B8] text-sm mb-1">Today</p>
            <p className="text-3xl font-bold text-[#22C55E]">{blogStats.summary.total_views_today || 0}</p>
          </div>
        </Card>
        <Card className="bg-[#121A2B] border-[#1E293B] p-6">
          <div>
            <p className="text-[#94A3B8] text-sm mb-1">This Week</p>
            <p className="text-3xl font-bold text-[#3B82F6]">{blogStats.summary.total_views_week || 0}</p>
          </div>
        </Card>
        <Card className="bg-[#121A2B] border-[#1E293B] p-6">
          <div>
            <p className="text-[#94A3B8] text-sm mb-1">Posts Tracked</p>
            <p className="text-3xl font-bold text-[#F59E0B]">{blogStats.summary.total_posts_tracked || 0}</p>
          </div>
        </Card>
      </div>

      {/* Top 5 Chart */}
      <Card className="bg-[#121A2B] border-[#1E293B] p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="text-[#3B82F6]" size={20} />
          <h3 className="text-lg font-semibold text-[#F8FAFC]">Top 5 Posts by Views</h3>
        </div>
        {chartData.length > 0 ? (
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis type="number" stroke="#94A3B8" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={200} 
                  stroke="#94A3B8"
                  tick={Y_AXIS_TICK_STYLE}
                />
                <Tooltip 
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value) => [value, 'Views']}
                  labelFormatter={(label, payload) => payload[0]?.payload?.fullTitle || label}
                />
                <Bar dataKey="views" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-[#94A3B8]">
            <div className="text-center">
              <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
              <p>No blog data yet</p>
              <p className="text-sm mt-2">Views will appear as readers visit your blog posts</p>
            </div>
          </div>
        )}
      </Card>

      {/* Full Stats Table */}
      <Card className="bg-[#121A2B] border-[#1E293B] overflow-hidden">
        <div className="p-4 border-b border-[#1E293B] flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#F8FAFC]">All Blog Posts</h3>
          <Button
            onClick={loadBlogStats}
            variant="outline"
            size="sm"
            className="border-[#1E293B] bg-[#0B1020] hover:bg-[#1E293B] text-[#F8FAFC]"
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
        {blogStats.posts.length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">
            <Eye size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg">No blog views recorded yet</p>
            <p className="text-sm mt-2">Statistics will appear when readers visit your blog</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1E293B]">
                  <TableHead className="text-[#94A3B8]">Post Title</TableHead>
                  <TableHead className="text-[#94A3B8]">Category</TableHead>
                  <TableHead className="text-[#94A3B8] text-center">Views</TableHead>
                  <TableHead className="text-[#94A3B8] text-center">Avg Read Time</TableHead>
                  <TableHead className="text-[#94A3B8]">Last Viewed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blogStats.posts.map((post, index) => (
                  <TableRow key={post.slug} className="border-[#1E293B]">
                    <TableCell className="text-[#F8FAFC] font-medium max-w-xs">
                      <div className="flex items-center gap-2">
                        {index < 3 && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                            index === 1 ? 'bg-gray-400/20 text-gray-300' :
                            'bg-amber-700/20 text-amber-600'
                          }`}>
                            #{index + 1}
                          </span>
                        )}
                        <span className="truncate">{post.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 text-xs rounded-full bg-[#3B82F6]/20 text-[#3B82F6]">
                        {post.category}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-[#F8FAFC] font-semibold">{post.total_views}</span>
                    </TableCell>
                    <TableCell className="text-center text-[#94A3B8]">
                      <div className="flex items-center justify-center gap-1">
                        <Clock size={14} />
                        {post.avg_read_time}
                      </div>
                    </TableCell>
                    <TableCell className="text-[#94A3B8]">
                      {formatLastViewed(post.last_viewed)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
};

// Leads Tab Component
const LeadsTab = ({ authToken }) => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filterStatus === 'all' ? '' : `?status_filter=${filterStatus}`;
      const response = await axios.get(`${API}/leads${statusParam}`, {
        headers: { Authorization: `Bearer ${authToken}` },
        withCredentials: true
      });
      setLeads(response.data);
      setFilteredLeads(response.data);

      const statsResponse = await axios.get(`${API}/leads/stats`, {
        headers: { Authorization: `Bearer ${authToken}` },
        withCredentials: true
      });
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLoading(false);
    }
  }, [authToken, filterStatus]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleStatusChange = useCallback(async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${authToken}` },
        withCredentials: true
      });
      loadLeads();
    } catch (error) {
      console.error('Error updating lead status:', error);
      alert('Failed to update lead status. Please try again.');
    }
  }, [authToken, loadLeads]);

  const handleExport = useCallback(() => {
    if (filteredLeads.length === 0) {
      alert('No leads to export');
      return;
    }

    const headers = ['ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Primary Goal', 'Interested In', 'Notes', 'Status', 'Date'];
    const rows = filteredLeads.map(lead => [
      lead.id,
      lead.firstName,
      lead.lastName,
      lead.email,
      lead.phone,
      lead.primaryGoal,
      lead.interestedIn,
      lead.notes || '',
      lead.status,
      lead.createdAt
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [filteredLeads]);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#121A2B] border-[#1E293B] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#94A3B8] text-sm mb-1">Total Leads</p>
              <p className="text-3xl font-bold text-[#F8FAFC]">{stats.total}</p>
            </div>
            <Users className="text-[#3B82F6]" size={32} />
          </div>
        </Card>
        <Card className="bg-[#121A2B] border-[#1E293B] p-6">
          <div>
            <p className="text-[#94A3B8] text-sm mb-1">New</p>
            <p className="text-3xl font-bold text-[#22C55E]">{stats.new}</p>
          </div>
        </Card>
        <Card className="bg-[#121A2B] border-[#1E293B] p-6">
          <div>
            <p className="text-[#94A3B8] text-sm mb-1">Contacted</p>
            <p className="text-3xl font-bold text-[#3B82F6]">{stats.contacted}</p>
          </div>
        </Card>
        <Card className="bg-[#121A2B] border-[#1E293B] p-6">
          <div>
            <p className="text-[#94A3B8] text-sm mb-1">Closed</p>
            <p className="text-3xl font-bold text-[#94A3B8]">{stats.closed}</p>
          </div>
        </Card>
      </div>

      {/* Filter & Export */}
      <div className="flex justify-between items-center">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48 bg-[#121A2B] border-[#1E293B] text-[#F8FAFC]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leads</SelectItem>
            <SelectItem value="New">New</SelectItem>
            <SelectItem value="Contacted">Contacted</SelectItem>
            <SelectItem value="Closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-3">
          <Button
            onClick={loadLeads}
            variant="outline"
            className="border-[#1E293B] bg-[#121A2B] hover:bg-[#1E293B] text-[#F8FAFC]"
            disabled={loading}
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleExport}
            className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
          >
            <Download size={18} className="mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Leads Table */}
      <Card className="bg-[#121A2B] border-[#1E293B] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#94A3B8]">
            <RefreshCw size={48} className="mx-auto mb-4 opacity-50 animate-spin" />
            <p className="text-lg">Loading leads...</p>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">
            <Users size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg">No leads yet</p>
            <p className="text-sm mt-2">Submissions will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1E293B]">
                  <TableHead className="text-[#94A3B8]">Name</TableHead>
                  <TableHead className="text-[#94A3B8]">Email</TableHead>
                  <TableHead className="text-[#94A3B8]">Phone</TableHead>
                  <TableHead className="text-[#94A3B8]">Goal</TableHead>
                  <TableHead className="text-[#94A3B8]">Interested In</TableHead>
                  <TableHead className="text-[#94A3B8]">Notes</TableHead>
                  <TableHead className="text-[#94A3B8]">Status</TableHead>
                  <TableHead className="text-[#94A3B8]">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => (
                  <TableRow key={lead.id} className="border-[#1E293B]">
                    <TableCell className="text-[#F8FAFC]">
                      {lead.firstName} {lead.lastName}
                    </TableCell>
                    <TableCell className="text-[#F8FAFC]">{lead.email}</TableCell>
                    <TableCell className="text-[#F8FAFC]">{lead.phone}</TableCell>
                    <TableCell className="text-[#94A3B8] capitalize">
                      {lead.primaryGoal?.replace('-', ' ')}
                    </TableCell>
                    <TableCell className="text-[#94A3B8] capitalize">
                      {lead.interestedIn?.replace('-', ' ')}
                    </TableCell>
                    <TableCell className="text-[#94A3B8] max-w-xs truncate">
                      {lead.notes || '-'}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={lead.status}
                        onValueChange={(value) => handleStatusChange(lead.id, value)}
                      >
                        <SelectTrigger className="w-32 bg-[#0B1020] border-[#1E293B] text-[#F8FAFC] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="New">New</SelectItem>
                          <SelectItem value="Contacted">Contacted</SelectItem>
                          <SelectItem value="Closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-[#94A3B8] text-sm">
                      {lead.createdAt}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
};

// Main Admin Dashboard Component
export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, false = not auth, true = auth
  const [authToken, setAuthToken] = useState(null);
  const [username, setUsername] = useState('');

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const storedToken = localStorage.getItem('admin_token');
      
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/admin/verify`, {
            headers: { Authorization: `Bearer ${storedToken}` },
            withCredentials: true
          });
          
          if (response.data.authenticated) {
            setAuthToken(storedToken);
            setUsername(response.data.username || localStorage.getItem('admin_username') || '');
            setIsAuthenticated(true);
            return;
          }
        } catch (err) {
          // Token invalid or verification failed, clear storage
          console.error('Session verification failed:', err);
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_username');
        }
      }
      
      setIsAuthenticated(false);
    };

    checkSession();
  }, []);

  const handleLogin = useCallback((data) => {
    setAuthToken(data.token);
    setUsername(data.username);
    setIsAuthenticated(true);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Logout API failed, but proceed to clear local session anyway
      console.error('Logout request failed:', err);
    }
    
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    setAuthToken(null);
    setUsername('');
    setIsAuthenticated(false);
  }, []);

  // Show loading while checking session
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#0B1020] flex items-center justify-center">
        <RefreshCw size={48} className="text-[#3B82F6] animate-spin" />
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  // Show dashboard if authenticated
  return (
    <div className="min-h-screen bg-[#0B1020] p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
                className="text-[#94A3B8] hover:text-[#F8FAFC]"
              >
                <ArrowLeft size={18} className="mr-2" />
                Back to Home
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">Admin Dashboard</h1>
            <p className="text-[#94A3B8]">Welcome back, <span className="text-[#F8FAFC] font-medium">{username}</span></p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-400"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="leads" className="w-full">
          <TabsList className="bg-[#121A2B] border border-[#1E293B] mb-6">
            <TabsTrigger 
              value="leads" 
              className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-white"
              data-testid="leads-tab"
            >
              <Users size={18} className="mr-2" />
              Leads
            </TabsTrigger>
            <TabsTrigger 
              value="blog-stats" 
              className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-white"
              data-testid="blog-stats-tab"
            >
              <BarChart3 size={18} className="mr-2" />
              Blog Stats
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <LeadsTab authToken={authToken} />
          </TabsContent>

          <TabsContent value="blog-stats">
            <BlogStatsTab authToken={authToken} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
